import { useRef, useState } from "react";
import api, { fmtError } from "@/lib/api";
import { toast } from "sonner";
import DateRangeBar from "@/components/DateRangeBar";
import { parseCSV, TEMPLATES, downloadText, runMassExport } from "@/lib/exportBundle";
import { Upload, Download, FileText, Archive, DatabaseBackup, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { NoAccess } from "@/components/Can";

const Section = ({ icon: Icon, title, desc, children, testid }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 space-y-4" data-testid={testid}>
    <div className="flex items-start gap-3"><div className="w-9 h-9 rounded bg-slate-900 text-white grid place-items-center shrink-0"><Icon size={16}/></div>
      <div><div className="text-base font-semibold" style={{fontFamily:'Plus Jakarta Sans'}}>{title}</div><div className="text-xs text-slate-500">{desc}</div></div></div>
    {children}
  </div>
);
const Btn = ({ children, onClick, testid, dark, disabled, danger }) => (
  <button data-testid={testid} onClick={onClick} disabled={disabled} className={`h-9 px-3 rounded-md text-xs font-semibold uppercase tracking-wider flex items-center gap-2 disabled:opacity-50 ${dark ? "bg-slate-900 hover:bg-slate-800 text-white" : danger ? "bg-rose-700 hover:bg-rose-800 text-white" : "border border-slate-300 bg-white hover:bg-slate-50"}`}>{children}</button>
);

function ImportSection() {
  const [type, setType] = useState("customers");
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const t = TEMPLATES[type];
  const template = () => downloadText(t.file, [t.header.join(","), t.sample.join(",")].join("\n"));
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name); setResult(null);
    const parsed = parseCSV(await f.text());
    setRows(parsed); if (!parsed.length) toast.error("No rows found in file");
  };
  const doImport = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/import/${type}`, { rows }); setResult(data); toast.success(`Imported: ${data.created} created, ${data.updated} updated`); setRows([]); setFileName(""); if (fileRef.current) fileRef.current.value = ""; }
    catch (err) { toast.error(fmtError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Section icon={Upload} title="Import Customers & Products" desc="Upload a CSV. Existing records (matched by name) are updated, new ones are created." testid="import-section">
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">Import type</label>
          <select data-testid="import-type" value={type} onChange={(e)=>{setType(e.target.value); setRows([]); setResult(null);}} className="mt-1 block h-9 px-2 border border-slate-300 rounded text-sm bg-white outline-none focus:border-blue-600"><option value="customers">Customers</option><option value="products">Products</option></select></div>
        <Btn testid="import-template-btn" onClick={template}><Download size={14}/> Sample template (.csv)</Btn>
        <label className="h-9 px-3 border border-slate-300 bg-white rounded-md text-xs font-semibold uppercase tracking-wider flex items-center gap-2 hover:bg-slate-50 cursor-pointer"><Upload size={14}/> Choose CSV
          <input ref={fileRef} data-testid="import-file" type="file" accept=".csv,text/csv" onChange={onFile} className="hidden"/></label>
        {fileName && <span className="text-xs text-slate-600 mono" data-testid="import-file-name">{fileName} · {rows.length} rows</span>}
        <Btn testid="import-run-btn" dark disabled={!rows.length || busy} onClick={doImport}>{busy ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Import {rows.length ? `${rows.length} rows` : ""}</Btn>
      </div>
      <div className="text-[11px] text-slate-500">Columns: <span className="mono">{t.header.join(", ")}</span>{type==="products" && " · enable_multiplier_breakdown: TRUE/FALSE"}</div>
      {rows.length > 0 && (
        <div className="border border-slate-200 rounded overflow-auto max-h-56" data-testid="import-preview">
          <table className="acct-table fit"><thead><tr>{t.header.map(h=><th key={h}>{h}</th>)}</tr></thead>
            <tbody>{rows.slice(0, 20).map((r,i)=><tr key={i}>{t.header.map(h=><td key={h} className="text-xs">{r[h]}</td>)}</tr>)}</tbody></table>
          {rows.length > 20 && <div className="px-3 py-1 text-[11px] text-slate-500">…and {rows.length - 20} more rows</div>}
        </div>
      )}
      {result && <div className="text-sm bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="import-result">Created <b>{result.created}</b> · Updated <b>{result.updated}</b>{result.errors?.length>0 && <div className="mt-1 text-rose-700 text-xs">{result.errors.slice(0,10).map((e,i)=><div key={i}>{e}</div>)}</div>}</div>}
    </Section>
  );
}

function ExportSection() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [include, setInclude] = useState({ invoices: true, receipts: true, journals: true, statements: true, reports: true, backup: true });
  const [prog, setProg] = useState(null);
  const [summary, setSummary] = useState(null);
  const run = async () => {
    setSummary(null); setProg({ phase: "Starting…", done: 0, total: 1 });
    try { const s = await runMassExport({ range, include, onProgress: setProg }); setSummary(s); toast.success(`Export ready: ${s.total} files in ${s.parts} zip${s.parts>1?"s":""}`); }
    catch (err) { toast.error("Export failed: " + (err.message || "unknown")); }
    finally { setTimeout(()=>setProg(null), 1500); }
  };
  const pct = prog ? Math.round((prog.done / Math.max(prog.total,1)) * 100) : 0;
  return (
    <Section icon={Archive} title="Mass Export (ZIP of PDFs)" desc="Exports every Invoice, Receipt, Journal Voucher, Customer Statement and all Reports as PDFs into organised folders. Large exports are split into parts of 250 documents each." testid="export-section">
      <DateRangeBar range={range} onChange={setRange} testid="export" />
      <div className="flex flex-wrap gap-3">
        {[["invoices","Invoices"],["receipts","Receipts"],["journals","Journal Vouchers"],["statements","Customer Statements"],["reports","Reports (Balances, Day Book, Sales)"],["backup","Full data backup (JSON)"]].map(([k,l]) => (
          <label key={k} className="flex items-center gap-2 text-sm"><input data-testid={`export-include-${k}`} type="checkbox" checked={include[k]} onChange={(e)=>setInclude({...include,[k]:e.target.checked})}/>{l}</label>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <Btn testid="export-run-btn" dark disabled={!!prog || !Object.values(include).some(Boolean)} onClick={run}>{prog ? <Loader2 size={14} className="animate-spin"/> : <FileText size={14}/>} Export everything</Btn>
        {prog && <div className="flex-1 max-w-md" data-testid="export-progress"><div className="text-[11px] text-slate-500 mb-1">{prog.phase} {prog.total>1 && `· ${prog.done}/${prog.total}`}</div><div className="h-2 bg-slate-200 rounded overflow-hidden"><div className="h-full bg-blue-600 transition-[width]" style={{width:`${pct}%`}}/></div></div>}
        {summary && !prog && <div className="text-xs text-emerald-800" data-testid="export-summary">{summary.total} files · {summary.parts} zip part(s){summary.failed.length ? ` · ${summary.failed.length} failed` : ""}</div>}
      </div>
      <div className="text-[11px] text-slate-500">Folders: <span className="mono">Invoices/ · Receipts/ · Journal Vouchers/ · Statements/ · Reports/ · Backup/</span> — statements use the selected date range; the backup is always complete.</div>
    </Section>
  );
}

function BackupSection() {
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const download = async () => {
    setBusy(true);
    try { const { data } = await api.get("/backup"); downloadText(`ledgerfast-backup-${new Date().toISOString().slice(0,10)}.json`, JSON.stringify(data, null, 1), "application/json"); toast.success(`Backup downloaded (${Object.values(data.counts).reduce((s,n)=>s+n,0)} records)`); }
    catch { toast.error("Backup failed"); } finally { setBusy(false); }
  };
  const restore = async () => {
    if (!file || confirm !== "RESTORE") return;
    setBusy(true);
    try { const body = JSON.parse(await file.text()); const { data } = await api.post("/restore", body); setResult(data.restored); toast.success("Restore complete — data replaced from backup"); setFile(null); setConfirm(""); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail) || "Invalid backup file"); } finally { setBusy(false); }
  };
  return (
    <Section icon={DatabaseBackup} title="Full Backup & Restore" desc="Backup includes customers, products, price lists, invoices, receipts, journal vouchers and company settings. Restore replaces ALL current data with the backup." testid="backup-section">
      <div className="flex flex-wrap items-center gap-3">
        <Btn testid="backup-download-btn" dark disabled={busy} onClick={download}><Download size={14}/> Download full backup (.json)</Btn>
      </div>
      <div className="border border-rose-200 bg-rose-50 rounded-md p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-rose-800"><AlertTriangle size={16}/> Restore from backup (destructive)</div>
        <div className="flex flex-wrap items-center gap-3">
          <input data-testid="restore-file" type="file" accept=".json,application/json" onChange={(e)=>{setFile(e.target.files?.[0]||null); setResult(null);}} className="text-xs"/>
          <input data-testid="restore-confirm" value={confirm} onChange={(e)=>setConfirm(e.target.value)} placeholder='Type RESTORE to confirm' className="h-9 px-3 border border-rose-300 rounded text-sm outline-none focus:border-rose-600 bg-white"/>
          <Btn testid="restore-run-btn" danger disabled={!file || confirm !== "RESTORE" || busy} onClick={restore}>{busy ? <Loader2 size={14} className="animate-spin"/> : <DatabaseBackup size={14}/>} Restore now</Btn>
        </div>
        {result && <div className="text-xs text-slate-700 mono" data-testid="restore-result">Restored: {Object.entries(result).map(([k,v])=>`${k} ${v}`).join(" · ")}</div>}
      </div>
    </Section>
  );
}

export default function DataHub() {
  const { can } = useAuth();
  if (!can("import_export")) return <NoAccess what="Import / Export" />;
  return (
    <div className="space-y-4" data-testid="data-page">
      <div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily:'Plus Jakarta Sans'}}>Import / Export</h1>
        <p className="text-sm text-slate-500">Bulk upload masters, export every document as PDF, and keep a full system backup.</p></div>
      <ImportSection />
      <ExportSection />
      <BackupSection />
    </div>
  );
}
